import React, { useState, useEffect } from "react";
import { Container, Table, Spinner } from "react-bootstrap";
import { Autocomplete, TextField } from "@mui/material";
import SubmitButton from "components/Buttons/SubmitButton";
import { EditButton } from "components/Buttons/EditButton";
import { DeleteButton } from "components/Buttons/DeleteButton";
import { fetchCategoryByDropDown } from "slices/category/thunk";
import { useDispatch } from "react-redux";
import { AppDispatch } from "slices/store";
import { fetchSubCategoryDropdownByCategoryId } from "slices/sub-category/thunk";
import { fetchItemsByCategoryAndSubCategory } from "slices/item/thunk";
import { autocompleteDropdownStyles, tableStyles } from "Constants/Style";
import { toast } from "react-toastify";
import DispatchBagSelectionModal from "./DispatchBagSelectionModal";
import DispatchBagSelectionModalStockTransfer from "./DispatchBagSelectionModalStockTransfer";
import ActionButton from "components/Buttons/ActionButton";
import SaveCancelButtons from "components/Buttons/SaveCancelButtons";
import { showConfirm } from "utils/swalHelper";

interface ItemOption {
  name: string;
  code: string;
  id: number;
  unit?: string;
  unit_id?: string;
}

interface CategoryOption {
  category_name: string;
  category_id?: string;
}

interface SubCategoryOption {
  sub_category_name: string;
  sub_category_id?: string;
}

interface FormData {
  category: string | null;
  category_id: string | null;
  subCategory: string | null;
  sub_category_id: string | null;
  itemName: string | null;
  item_id: string | null;
  itemCode: string | null;
  unit: string | null;
  unit_id: string | null;
  bags: BagData[]; // Array of bags
  dispatch_quantity: number; // Total dispatch quantity (required before bag selection)
  remarks: string;
}

interface LoadingStates {
  items: boolean;
  categories: boolean;
  subcategories: boolean;
}

interface BagData {
  bag_no: string;
  inventory_item_id: string | number;
  total_quantity: number;
  bag_item_dispatch_quantity: number;
}

// Interface matching what the modal returns
interface BagWithoutId {
  inventory_item_id: string | number;
  bag_no: string;
  total_quantity: number;
  bag_item_dispatch_quantity: number;
}

interface DispatchRowData {
  id: string;
  item_id?: string;
  itemName: string;
  itemCode: string;
  category: string;
  category_id?: string;
  subCategory: string;
  sub_category_id?: string;
  unit: string;
  unit_id?: string;
  bags: BagData[]; // Array of bags instead of single bag_no
  dispatch_quantity: number; // Total dispatch quantity (sum of all bags) - required before bag selection
  available_quantity?: number; // Available quantity for scanned items
  isScanned?: boolean; // Flag to identify scanned items
  remarks: string;
}

interface PropsTypes {
  onItemsUpdated?: (items: DispatchRowData[]) => void;
  initialItems?: DispatchRowData[];
  showBagNo?: boolean; // Optional: show/hide bag number field
  transferFromWarehouseId?: string; // Optional: transfer from warehouse ID for validation
  isStockTransfer?: boolean; // Optional: indicates if this is for stock transfer dispatch
  isEditMode?: boolean; // Optional: indicates if this is in edit mode
}

const ConfigureDispatchItems = ({
  onItemsUpdated,
  initialItems,
  showBagNo = true,
  transferFromWarehouseId,
  isStockTransfer = false,
  isEditMode = false,
}: PropsTypes) => {
  const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState<
    SubCategoryOption[]
  >([]);
  const [itemOptions, setItemOptions] = useState<ItemOption[]>([]);
  const [rows, setRows] = useState<DispatchRowData[]>([]);
  const [bagModalOpen, setBagModalOpen] = useState(false);
  const [bagModalRowId, setBagModalRowId] = useState<string | null>(null);
  const [bagModalReadOnly, setBagModalReadOnly] = useState(false);

  // Initialize rows with initialItems if provided (for edit mode)
  // Also clear rows when initialItems becomes undefined/empty
  useEffect(() => {
    if (initialItems && initialItems.length > 0) {
      // Only sync if rows are empty or if initialItems has actually changed
      // Use a more reliable comparison by checking item IDs and key properties
      const hasChanged = rows.length === 0 || 
        rows.length !== initialItems.length ||
        rows.some((row, idx) => {
          const initialItem = initialItems[idx];
          return !initialItem || 
            row.id !== initialItem.id ||
            row.dispatch_quantity !== initialItem.dispatch_quantity ||
            JSON.stringify(row.bags) !== JSON.stringify(initialItem.bags) ||
            row.remarks !== initialItem.remarks;
        });
      
      if (hasChanged) {
        setRows(initialItems);
      }
    } else if (initialItems === undefined && rows.length > 0) {
      // Clear rows when initialItems becomes undefined
      setRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialItems]);

  const [openCategory, setOpenCategory] = useState(false);
  const [openSubCategory, setOpenSubCategory] = useState(false);
  const [openItemName, setOpenItemName] = useState(false);

  const dispatch = useDispatch<AppDispatch>();

  const [formData, setFormData] = useState<FormData>({
    category: null,
    category_id: null,
    subCategory: null,
    sub_category_id: null,
    itemName: null,
    item_id: null,
    itemCode: null,
    unit: null,
    unit_id: null,
    bags: [],
    dispatch_quantity: 0,
    remarks: "",
  });

  const [loading, setLoading] = useState<LoadingStates>({
    categories: false,
    subcategories: false,
    items: false,
  });

  const [adding, setAdding] = useState(false);
  const [deletingRows, setDeletingRows] = useState<string[]>([]);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editingRowData, setEditingRowData] = useState<DispatchRowData | null>(
    null
  );

  const fetchCategories = async (): Promise<void> => {
    setLoading((prev) => ({ ...prev, categories: true }));
    try {
      const res = await dispatch(fetchCategoryByDropDown()).unwrap();
      setCategoryOptions(res.data || []);
    } catch {
      console.error("Failed to fetch categories");
    } finally {
      setLoading((prev) => ({ ...prev, categories: false }));
    }
  };

  const fetchSubcategories = async (categoryId: string): Promise<void> => {
    setLoading((l) => ({ ...l, subcategories: true }));
    try {
      const res = await dispatch(
        fetchSubCategoryDropdownByCategoryId(categoryId)
      ).unwrap();
      setSubCategoryOptions(
        res?.data.map((s: any) => ({
          sub_category_name: s.sub_category_name,
          sub_category_id: s.sub_category_id,
        }))
      );
    } catch {
      setSubCategoryOptions([]);
    } finally {
      setLoading((l) => ({ ...l, subcategories: false }));
    }
  };

  const fetchItems = async (categoryId: string, subCategoryId: string) => {
    setLoading((prev) => ({ ...prev, items: true }));

    try {
      const items = await dispatch(
        fetchItemsByCategoryAndSubCategory({ categoryId, subCategoryId })
      ).unwrap();
      setItemOptions(items);
    } catch {
      setItemOptions([]);
    } finally {
      setLoading((prev) => ({ ...prev, items: false }));
    }
  };

  // Calculate total quantity from bags
  const calculateTotalQuantity = (bags: BagData[]): number => {
    return bags.reduce(
      (sum, bag) => sum + (bag.bag_item_dispatch_quantity || 0),
      0
    );
  };

  const handleAutocompleteChange = (
    key: keyof FormData,
    value: ItemOption | CategoryOption | SubCategoryOption | null
  ): void => {
    let updatedFormData = { ...formData };

    if (key === "category") {
      if (!value) {
        updatedFormData = {
          ...updatedFormData,
          category: null,
          category_id: null,
          subCategory: null,
          sub_category_id: null,
          itemName: null,
          item_id: null,
          itemCode: null,
          unit: null,
          unit_id: null,
          bags: [], // Clear bags when category changes
          dispatch_quantity: 0,
        };
        setSubCategoryOptions([]);
        setItemOptions([]);
      } else {
        const cat = value as CategoryOption;
        updatedFormData.category = cat.category_name;
        updatedFormData.category_id = cat.category_id || null;
        updatedFormData.subCategory = null;
        updatedFormData.sub_category_id = null;
        updatedFormData.bags = []; // Clear bags when category changes
        updatedFormData.dispatch_quantity = 0;
        setItemOptions([]);
      }
    }

    if (key === "subCategory") {
      if (!value) {
        updatedFormData = {
          ...updatedFormData,
          subCategory: null,
          sub_category_id: null,
          itemName: null,
          item_id: null,
          itemCode: null,
          unit: null,
          unit_id: null,
          bags: [], // Clear bags when subcategory changes
          dispatch_quantity: 0,
        };
        setItemOptions([]);
      } else {
        const sub = value as SubCategoryOption;
        updatedFormData.subCategory = sub.sub_category_name;
        updatedFormData.sub_category_id = sub.sub_category_id || null;
        updatedFormData.bags = []; // Clear bags when subcategory changes
        updatedFormData.dispatch_quantity = 0;
      }
    }

    if (key === "itemName") {
      if (!value) {
        updatedFormData.itemName = null;
        updatedFormData.item_id = null;
        updatedFormData.itemCode = null;
        updatedFormData.unit = null;
        updatedFormData.unit_id = null;
        updatedFormData.bags = [];
        updatedFormData.dispatch_quantity = 0;
      } else {
        const item = value as ItemOption;
        updatedFormData.itemName = item.name;
        updatedFormData.item_id = String(item.id) || null;
        updatedFormData.itemCode = item.code;
        updatedFormData.unit = item.unit || null;
        updatedFormData.unit_id = item.unit_id || null;
        // Clear bags when changing to a different item
        updatedFormData.bags = [];
        updatedFormData.dispatch_quantity = 0;
      }
    }

    setFormData(updatedFormData);
  };

  const handleChange = (key: keyof FormData, value: string | number): void => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleOpenBagModal = (rowId?: string, readOnly: boolean = false) => {
    if (rowId) {
      // Opening for existing row
      const row = rows.find((r) => r.id === rowId);
      if (!readOnly) {
        // Only validate for edit mode
        if (!row || !row.dispatch_quantity || row.dispatch_quantity <= 0) {
          toast.warning("Please enter Dispatch Quantity first");
          return;
        }
      }
      setBagModalRowId(rowId);
    } else {
      // Opening for new item (formData)
      if (!formData.itemName || !formData.category || !formData.subCategory) {
        toast.warning(
          "Please select Category, Sub Category, and Item Name first"
        );
        return;
      }
      if (!transferFromWarehouseId) {
        toast.warning("Please select Transfer From warehouse first");
        return;
      }
      if (!formData.dispatch_quantity || formData.dispatch_quantity <= 0) {
        toast.warning("Please enter Dispatch Quantity first");
        return;
      }
      setBagModalRowId(null);
    }
    setBagModalReadOnly(readOnly);
    setBagModalOpen(true);
  };

  const handleBagModalSave = (bags: BagWithoutId[]) => {
    // Map BagWithoutId[] to BagData[] (they have the same structure, so just cast)
    const bagDataArray: BagData[] = bags.map((bag) => ({
      bag_no: bag.bag_no,
      inventory_item_id: bag.inventory_item_id,
      total_quantity: bag.total_quantity,
      bag_item_dispatch_quantity: bag.bag_item_dispatch_quantity,
    }));
    const totalBagDispatchQty = calculateTotalQuantity(bagDataArray);

    if (bagModalRowId) {
      // Update existing row
      const row = rows.find((r) => r.id === bagModalRowId);
      if (row) {
        // Use editingRowData dispatch_quantity if row is being edited, otherwise use row.dispatch_quantity
        const dispatchQty = editingRowId === bagModalRowId && editingRowData
          ? editingRowData.dispatch_quantity
          : row.dispatch_quantity;
        
        // Validate that sum of bag dispatch quantities equals row dispatch_quantity
        if (Math.abs(totalBagDispatchQty - dispatchQty) > 0.01) {
          toast.error(
            `Sum of bag dispatch quantities (${totalBagDispatchQty}) must equal Dispatch Quantity (${dispatchQty})`
          );
          return;
        }
        const updatedRow: DispatchRowData = {
          ...row,
          bags: bagDataArray,
        };
        setRows((prev) => {
          const updated = prev.map((r) =>
            r.id === bagModalRowId ? updatedRow : r
          );
          if (onItemsUpdated) {
            onItemsUpdated(updated);
          }
          return updated;
        });
        
        // Also update editingRowData if this row is being edited
        if (editingRowId === bagModalRowId && editingRowData) {
          setEditingRowData({
            ...editingRowData,
            bags: bagDataArray,
          });
        }
      }
    } else {
      // Update formData for new item
      // Validate that sum of bag dispatch quantities equals formData dispatch_quantity
      if (Math.abs(totalBagDispatchQty - formData.dispatch_quantity) > 0.01) {
        toast.error(
          `Sum of bag dispatch quantities (${totalBagDispatchQty}) must equal Dispatch Quantity (${formData.dispatch_quantity})`
        );
        return;
      }
      setFormData((prev) => ({
        ...prev,
        bags: bagDataArray,
      }));
    }
    setBagModalOpen(false);
    setBagModalRowId(null);
  };

  const handleAdd = (): void => {
    // Validate that transfer from warehouse is selected
    if (!transferFromWarehouseId || transferFromWarehouseId.trim() === "") {
      toast.error("Please select Transfer From warehouse before adding items");
      return;
    }

    if (!formData.itemName || !formData.category || !formData.subCategory) {
      toast.warning(
        "Please select all required fields (Category, Sub Category, and Item Name)"
      );
      return;
    }

    // Validate dispatch quantity
    if (!formData.dispatch_quantity || formData.dispatch_quantity <= 0) {
      toast.warning("Dispatch Quantity is required and must be greater than 0");
      return;
    }

    // Validate bags are selected
    if (showBagNo && (!formData.bags || formData.bags.length === 0)) {
      toast.warning("Please select at least one bag");
      return;
    }

    // Validate that sum of bag dispatch quantities equals dispatch_quantity
    if (showBagNo && formData.bags.length > 0) {
      const totalBagDispatchQty = calculateTotalQuantity(formData.bags);
      if (Math.abs(totalBagDispatchQty - formData.dispatch_quantity) > 0.01) {
        toast.error(
          `Sum of bag dispatch quantities (${totalBagDispatchQty}) must equal Dispatch Quantity (${formData.dispatch_quantity})`
        );
        return;
      }
    }

    // Check if the same item already exists (same category, subCategory, itemName, itemCode)
    const existingItem = rows.find((row) => {
      return (
        row.category === formData.category &&
        row.subCategory === formData.subCategory &&
        row.itemName === formData.itemName &&
        row.itemCode === formData.itemCode
      );
    });

    if (existingItem) {
      toast.warning(
        `Item "${formData.itemName}" already exists. Please edit the existing item to update the bags.`
      );
      return;
    }

    setAdding(true);

    setTimeout(() => {
      const newRow: DispatchRowData = {
        id: Date.now().toString(),
        item_id: formData.item_id || undefined,
        itemName: formData.itemName!,
        itemCode: formData.itemCode || "",
        category: formData.category!,
        category_id: formData.category_id || undefined,
        subCategory: formData.subCategory!,
        sub_category_id: formData.sub_category_id || undefined,
        unit: formData.unit || "",
        unit_id: formData.unit_id || undefined,
        bags: formData.bags || [],
        dispatch_quantity: formData.dispatch_quantity,
        remarks: formData.remarks || "",
      };

      setRows((prev) => {
        const updatedRows = [...prev, newRow];
        if (onItemsUpdated) {
          onItemsUpdated(updatedRows);
        }
        return updatedRows;
      });

      setFormData({
        category: null,
        category_id: null,
        subCategory: null,
        sub_category_id: null,
        itemName: null,
        item_id: null,
        itemCode: null,
        unit: null,
        unit_id: null,
        bags: [],
        dispatch_quantity: 0,
        remarks: "",
      });

      setSubCategoryOptions([]);
      setItemOptions([]);

      setAdding(false);
    }, 400);
  };

  const handleDelete = (id: string): void => {
    const rowToDelete = rows.find((r) => r.id === id);
    const itemName = rowToDelete?.itemName || "this item";
    
    showConfirm(
      `Are you sure you want to delete item "${itemName}"? This action cannot be undone.`,
      "Delete Item?",
      "Yes, Delete",
      "Cancel"
    ).then((result) => {
      if (result.isConfirmed) {
        setDeletingRows((prev) => [...prev, id]);

        setTimeout(() => {
          setRows((prev) => {
            const updated = prev.filter((r) => r.id !== id);
            if (onItemsUpdated) {
              onItemsUpdated(updated);
            }
            return updated;
          });
          setDeletingRows((prev) => prev.filter((x) => x !== id));
          toast.success(`Item "${itemName}" deleted successfully`);
        }, 300);
      }
    });
  };

  const handleEdit = async (row: DispatchRowData): Promise<void> => {
    setEditingRowId(row.id);
    setEditingRowData({ ...row });
    // No need to fetch categories/subcategories/items/bags since they're not editable
  };

  const handleSaveEdit = (): void => {
    if (!editingRowData) return;

    // Validate dispatch quantity
    if (
      !editingRowData.dispatch_quantity ||
      editingRowData.dispatch_quantity <= 0
    ) {
      toast.error("Dispatch Quantity is required and must be greater than 0");
      return;
    }

    // Validate that total bag dispatch quantity equals dispatch quantity (if bags are shown)
    if (showBagNo && editingRowData.bags && editingRowData.bags.length > 0) {
      const totalBagDispatchQty = calculateTotalQuantity(editingRowData.bags);
      if (Math.abs(totalBagDispatchQty - editingRowData.dispatch_quantity) > 0.01) {
        toast.error(
          `Sum of bag dispatch quantities (${totalBagDispatchQty}) must equal Dispatch Quantity (${editingRowData.dispatch_quantity})`
        );
        return;
      }
    }

    // Note: For stock transfer, dispatch_quantity validation is done against bags
    // No need to validate against available_quantity here as it's handled in bag modal

    setRows((prev) => {
      const updated = prev.map((r) =>
        r.id === editingRowId ? editingRowData : r
      );
      if (onItemsUpdated) {
        onItemsUpdated(updated);
      }
      return updated;
    });

    setEditingRowId(null);
    setEditingRowData(null);

    // Show success toast
    toast.success("Item updated successfully");
  };

  const handleCancelEdit = (): void => {
    setEditingRowId(null);
    setEditingRowData(null);
    toast.warning("Item update cancelled");
  };

  const handleEditFieldChange = (
    key: keyof DispatchRowData,
    value: string | number
  ): void => {
    if (!editingRowData) return;

    // Only allow editing dispatch_quantity and remarks
    if (key !== "dispatch_quantity" && key !== "remarks") {
      return;
    }

    // Note: For stock transfer, dispatch_quantity validation is done against bags
    // No need to validate against available_quantity here as it's handled in bag modal

    setEditingRowData({ ...editingRowData, [key]: value });
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  console.log("Rendering ConfigureDispatchItems with rows:", rows);
  return (
    <Container fluid className="mx-none p-0 mt-3">
      <style>
        {`
          .sticky-column {
            position: relative;
          }
          .sticky-column::before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 1px;
            background-color: #dee2e6;
            z-index: 12;
          }
          thead .sticky-column::before {
            z-index: 13;
          }
        `}
      </style>
      <Table hover responsive className="align-middle text-center">
        <thead>
          <tr>
            <th style={{ ...tableStyles.headerAlt, minWidth: "70px" }}>
              Sr No.
            </th>
            <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>
              Category
            </th>
            <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>
              Sub Category
            </th>
            <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
              Items
            </th>
            <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>
              Item Code
            </th>
            <th style={{ ...tableStyles.headerAlt, minWidth: "100px" }}>
              Unit
            </th>
            <th style={{ ...tableStyles.headerAlt, minWidth: "180px" }}>
              Dispatch Quantity
            </th>
            {showBagNo && (
              <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>
                Bags
              </th>
            )}
            <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>
              Remarks
            </th>
            <th
              className="sticky-column"
              style={{
                ...tableStyles.headerAlt,
                ...tableStyles.actionHeader,
                minWidth: "100px",
              }}
            >
              Action
            </th>
          </tr>
        </thead>

        <tbody>
          {/* Add Row */}
          <tr>
            <td>Select Item</td>

            {/* Category */}
            <td>
              <Autocomplete
                open={openCategory}
                onOpen={() => setOpenCategory(true)}
                onClose={() => setOpenCategory(false)}
                size="small"
                options={categoryOptions}
                getOptionLabel={(opt) => opt.category_name || ""}
                value={
                  categoryOptions.find(
                    (c) => c.category_name === formData.category
                  ) || null
                }
                disabled={editingRowId !== null}
                loading={loading.categories}
                onChange={(_, value) => {
                  handleAutocompleteChange("category", value);
                  if (value?.category_id) fetchSubcategories(value.category_id);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Category"
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      "& .MuiInputBase-root": {
                        height: "32px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "6px 12px",
                        height: "20px",
                      },
                    }}
                  />
                )}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.category_name || ""
                  )
                }
                ListboxProps={{
                  ...autocompleteDropdownStyles.ListboxProps,
                  style: {
                    maxHeight: "200px",
                    overflow: "auto",
                    zIndex: 100,
                  },
                }}
                componentsProps={{
                  popper: {
                    style: {
                      zIndex: 100,
                    },
                    modifiers: [
                      {
                        name: "preventOverflow",
                        enabled: true,
                      },
                    ],
                  },
                }}
              />
            </td>

            {/* Sub Category */}
            <td>
              <Autocomplete
                open={openSubCategory}
                onOpen={() => setOpenSubCategory(true)}
                onClose={() => setOpenSubCategory(false)}
                size="small"
                options={subCategoryOptions}
                getOptionLabel={(opt) => opt.sub_category_name || ""}
                value={
                  subCategoryOptions.find(
                    (s) => s.sub_category_name === formData.subCategory
                  ) || null
                }
                disabled={!formData.category || editingRowId !== null}
                loading={loading.subcategories}
                onChange={(_, value) => {
                  handleAutocompleteChange("subCategory", value);
                  if (value?.sub_category_id && formData.category_id) {
                    fetchItems(formData.category_id, value.sub_category_id);
                  }
                  if (!value) setItemOptions([]);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      !formData.category
                        ? "Select Category First"
                        : "Select Sub Category"
                    }
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      "& .MuiInputBase-root": {
                        height: "32px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "6px 12px",
                        height: "20px",
                      },
                    }}
                  />
                )}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.sub_category_name || ""
                  )
                }
                ListboxProps={{
                  ...autocompleteDropdownStyles.ListboxProps,
                  style: {
                    maxHeight: "200px",
                    overflow: "auto",
                    zIndex: 100,
                  },
                }}
                componentsProps={{
                  popper: {
                    style: {
                      zIndex: 100,
                    },
                    modifiers: [
                      {
                        name: "preventOverflow",
                        enabled: true,
                      },
                    ],
                  },
                }}
              />
            </td>

            {/* Item Name - disabled until category and subcategory are selected */}
            <td>
              <Autocomplete
                open={openItemName}
                onOpen={() => setOpenItemName(true)}
                onClose={() => setOpenItemName(false)}
                size="small"
                options={itemOptions}
                getOptionLabel={(opt) => opt.name || ""}
                value={
                  itemOptions.find((o) => o.name === formData.itemName) || null
                }
                disabled={!formData.subCategory || editingRowId !== null}
                loading={loading.items}
                onChange={(_, value) =>
                  handleAutocompleteChange("itemName", value)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      !formData.subCategory
                        ? "Select Category & Sub Category First"
                        : "Select Item"
                    }
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      "& .MuiInputBase-root": {
                        height: "32px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "6px 12px",
                        height: "20px",
                      },
                    }}
                  />
                )}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.name || ""
                  )
                }
                ListboxProps={{
                  ...autocompleteDropdownStyles.ListboxProps,
                  style: {
                    maxHeight: "200px",
                    overflow: "auto",
                    zIndex: 100,
                  },
                }}
                componentsProps={{
                  popper: {
                    style: {
                      zIndex: 100,
                    },
                    modifiers: [
                      {
                        name: "preventOverflow",
                        enabled: true,
                      },
                    ],
                  },
                }}
              />
            </td>

            {/* Item Code */}
            <td>
              <TextField
                size="small"
                value={formData.itemCode || ""}
                disabled
                placeholder="Code"
                sx={{
                  ...autocompleteDropdownStyles.inputSx,
                  "& .MuiInputBase-root": {
                    height: "32px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "6px 12px",
                    height: "20px",
                  },
                }}
              />
            </td>

            {/* Unit */}
            <td>
              <TextField
                size="small"
                value={formData.unit || ""}
                disabled
                placeholder="Unit"
                sx={{
                  ...autocompleteDropdownStyles.inputSx,
                  "& .MuiInputBase-root": {
                    height: "32px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "6px 12px",
                    height: "20px",
                  },
                }}
              />
            </td>

            {/* Dispatch Quantity */}
            <td>
              <TextField
                size="small"
                type="number"
                value={formData.dispatch_quantity}
                onChange={(e) =>
                  handleChange("dispatch_quantity", e.target.value)
                }
                disabled={editingRowId !== null}
                placeholder="Dispatch Qty"
                inputProps={{
                  min: 0,
                }}
                sx={{
                  ...autocompleteDropdownStyles.inputSx,
                  "& .MuiInputBase-root": {
                    height: "32px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "6px 12px",
                    height: "20px",
                  },
                }}
              />
            </td>

            {/* Bags - Select Bags Button */}
            {showBagNo && (
              <td>
                <ActionButton
                  label={
                    formData.bags.length > 0
                      ? `${formData.bags.length} Bag(s)`
                      : "Select"
                  }
                  icon="bi bi-bag"
                  handleClick={() => handleOpenBagModal()}
                  variant="primary"
                  disabled={
                    editingRowId !== null ||
                    !formData.itemName ||
                    !formData.category ||
                    !formData.subCategory ||
                    !transferFromWarehouseId ||
                    !formData.dispatch_quantity ||
                    formData.dispatch_quantity <= 0
                  }
                />
              </td>
            )}

            {/* Remarks */}
            <td>
              <TextField
                size="small"
                value={formData.remarks}
                onChange={(e) => handleChange("remarks", e.target.value)}
                disabled={editingRowId !== null}
                placeholder="Remarks"
                sx={{
                  ...autocompleteDropdownStyles.inputSx,
                  "& .MuiInputBase-root": {
                    height: "32px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "6px 12px",
                    height: "20px",
                  },
                }}
              />
            </td>

            {/* Action Button */}
            <td className="sticky-column" style={tableStyles.actionCell}>
              <div style={tableStyles.actionButtonContainer}>
                <SubmitButton
                  isSubmitting={adding}
                  onClick={handleAdd}
                  disabled={
                    editingRowId !== null ||
                    !transferFromWarehouseId ||
                    transferFromWarehouseId.trim() === ""
                  }
                />
              </div>
            </td>
          </tr>

          {/* Existing Rows */}
          {rows.map((row, idx) => {
            const isEditing = editingRowId === row.id;
            const displayData =
              isEditing && editingRowData ? editingRowData : row;

            return (
              <tr key={row.id} style={isEditing ? tableStyles.rowEditing : {}}>
                <td style={tableStyles.cell}>{idx + 1}</td>

                {/* Category */}
                <td style={tableStyles.cell}>
                  <span>{row.category}</span>
                </td>

                {/* Sub Category */}
                <td style={tableStyles.cell}>
                  <span>{row.subCategory}</span>
                </td>

                {/* Item Name */}
                <td style={tableStyles.cell}>
                  <span>{row.itemName}</span>
                </td>

                {/* Item Code */}
                <td style={tableStyles.cell}>
                  <span>{row.itemCode}</span>
                </td>

                {/* Unit */}
                <td style={tableStyles.cell}>
                  <span>{row.unit || ""}</span>
                </td>

                {/* Dispatch Quantity */}
                <td style={tableStyles.cell}>
                  {isEditing ? (
                    <TextField
                      size="small"
                      type="number"
                      value={displayData.dispatch_quantity || 0}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        handleEditFieldChange("dispatch_quantity", value);
                      }}
                      inputProps={{
                        min: 0,
                      }}
                      fullWidth
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiInputBase-root": {
                          height: "32px",
                        },
                        "& .MuiInputBase-input": {
                          padding: "6px 12px",
                          height: "20px",
                        },
                      }}
                    />
                  ) : (
                    <span>{row.dispatch_quantity || 0}</span>
                  )}
                </td>

                {/* Bags - Select Bags Button or View Icon */}
                {showBagNo && (
                  <td style={tableStyles.cell}>
                    {isEditing ? (
                      <ActionButton
                        label={
                          row.bags && row.bags.length > 0
                            ? `${row.bags.length} Bag(s)`
                            : "Select Bags"
                        }
                        icon="bi bi-bag"
                        handleClick={() => handleOpenBagModal(row.id, false)}
                        variant="primary"
                        disabled={false}
                      />
                    ) : (
                      <div
                        onClick={() => handleOpenBagModal(row.id, true)}
                        style={{
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "4px 8px",
                          color: "#3969E7",
                        }}
                        title="View Bag Details"
                      >
                        <i className="bi bi-eye" style={{ fontSize: "18px" }}></i>
                        {row.bags && row.bags.length > 0 && (
                          <span style={{ marginLeft: "4px", fontSize: "14px" }}>
                            ({row.bags.length})
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                )}

                {/* Remarks */}
                <td style={tableStyles.cell}>
                  {isEditing ? (
                    <TextField
                      size="small"
                      value={displayData.remarks || ""}
                      onChange={(e) =>
                        handleEditFieldChange("remarks", e.target.value)
                      }
                      fullWidth
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiInputBase-root": {
                          height: "32px",
                        },
                        "& .MuiInputBase-input": {
                          padding: "6px 12px",
                          height: "20px",
                        },
                      }}
                    />
                  ) : (
                    <span>{row.remarks || "-"}</span>
                  )}
                </td>

                {/* Actions */}
                <td
                  style={
                    isEditing
                      ? tableStyles.actionCellEditing
                      : tableStyles.actionCell
                  }
                  className="sticky-column"
                >
                  {isEditing ? (
                    <SaveCancelButtons
                      onSave={handleSaveEdit}
                      onCancel={handleCancelEdit}
                      containerStyle={tableStyles.actionButtonContainer}
                    />
                  ) : (
                    <div style={tableStyles.actionButtonContainer}>
                      <EditButton
                        onClick={() => handleEdit(row)}
                        iconClassName="ph-pencil"
                      />
                      <DeleteButton
                        onClick={() => handleDelete(row.id)}
                        iconClassName="ph-trash"
                        isLoading={deletingRows.includes(row.id)}
                      >
                        {deletingRows.includes(row.id) && (
                          <Spinner animation="border" size="sm" />
                        )}
                      </DeleteButton>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {/* Bag Selection Modal */}
      {bagModalOpen && (
        isStockTransfer ? (
          <DispatchBagSelectionModalStockTransfer
            key={`bag-modal-${bagModalRowId || 'new'}-${bagModalRowId ? rows.find((r) => r.id === bagModalRowId)?.item_id : formData.item_id}`}
            show={bagModalOpen}
            handleClose={() => {
              setBagModalOpen(false);
              setBagModalRowId(null);
              setBagModalReadOnly(false);
            }}
            quantity={
              bagModalRowId
                ? (editingRowId === bagModalRowId && editingRowData
                    ? editingRowData.dispatch_quantity
                    : rows.find((r) => r.id === bagModalRowId)?.dispatch_quantity) || 0
                : formData.dispatch_quantity || 0
            }
            onSave={handleBagModalSave}
            initialBags={
              bagModalRowId
                ? (editingRowId === bagModalRowId && editingRowData
                    ? editingRowData.bags
                    : rows.find((r) => r.id === bagModalRowId)?.bags) || []
                : formData.bags || []
            }
            itemName={
              bagModalRowId
                ? rows.find((r) => r.id === bagModalRowId)?.itemName
                : formData.itemName || undefined
            }
            categoryName={
              bagModalRowId
                ? rows.find((r) => r.id === bagModalRowId)?.category
                : formData.category || undefined
            }
            subCategoryName={
              bagModalRowId
                ? rows.find((r) => r.id === bagModalRowId)?.subCategory
                : formData.subCategory || undefined
            }
            warehouseId={transferFromWarehouseId}
          />
        ) : (
          <DispatchBagSelectionModal
            key={`bag-modal-${bagModalRowId || 'new'}-${bagModalRowId ? rows.find((r) => r.id === bagModalRowId)?.item_id : formData.item_id}`}
            show={bagModalOpen}
            handleClose={() => {
              setBagModalOpen(false);
              setBagModalRowId(null);
              setBagModalReadOnly(false);
            }}
            quantity={
              bagModalRowId
                ? (editingRowId === bagModalRowId && editingRowData
                    ? editingRowData.dispatch_quantity
                    : rows.find((r) => r.id === bagModalRowId)?.dispatch_quantity) || 0
                : formData.dispatch_quantity || 0
            }
            onSave={handleBagModalSave}
            initialBags={
              bagModalRowId
                ? (editingRowId === bagModalRowId && editingRowData
                    ? editingRowData.bags
                    : rows.find((r) => r.id === bagModalRowId)?.bags) || []
                : formData.bags || []
            }
            itemName={
              bagModalRowId
                ? rows.find((r) => r.id === bagModalRowId)?.itemName
                : formData.itemName || undefined
            }
            categoryName={
              bagModalRowId
                ? rows.find((r) => r.id === bagModalRowId)?.category
                : formData.category || undefined
            }
            subCategoryName={
              bagModalRowId
                ? rows.find((r) => r.id === bagModalRowId)?.subCategory
                : formData.subCategory || undefined
            }
            warehouseId={transferFromWarehouseId}
            isStockTransfer={isStockTransfer}
            readOnly={bagModalReadOnly}
            isEditMode={isEditMode}
          />
        )
      )}
    </Container>
  );
};

export default ConfigureDispatchItems;
